// EDPR document DAO — lightweight read operations on edpr_documents.
// Write operations (upsert, sync) live in EdprDocumentSyncService.

import { BaseDao, type SupabaseClient } from '../base.ts';
import type { PostgrestError } from '@supabase/supabase-js';
import type { EdprDocument, EdprRecord } from '../../types/edpr.ts';

// ── EdprDocumentDao ──────────────────────────────────────────────────────

export class EdprDocumentDao extends BaseDao<{ id: number }> {
  protected readonly dependentResources = ['edpr_documents'];

  constructor(sb: SupabaseClient) {
    super(sb, 'edpr_documents');
  }

  /**
   * Fetch the full document payload needed to send to BDO:
   * document row + organizacja_odzysku company join.
   */
  async getFullDocumentForBdo(documentId: number): Promise<EdprDocument | null> {
    const { data, error }: { data: EdprDocument | null; error: PostgrestError | null } = await this.sb
      .from('edpr_documents')
      .select('*, organizacja_odzysku_company:organizacja_odzysku_company_id(*)')
      .eq('id', documentId)
      .single();
    if (error) throw error;
    return data;
  }

  /**
   * Fetch all EDPR records for a document (for BDO submission).
   * Includes nested company joins expected by BdoEdprRecordPayload.
   */
  async getRecordsForBdo(documentId: number): Promise<EdprRecord[]> {
    const { data, error } = await this.sb
      .from('edpr_records')
      .select(`*,
        transactions:transaction_id(*, load_place_company:load_place_company_id(*), unload_place_company:unload_place_company_id(*), exporter_company:exporter_company_id(*), importer_company:importer_company_id(*), load_commercial_company:load_commercial_company_id(*), unload_commercial_company:unload_commercial_company_id(*), products:product_id(waste_code)),
        load_commercial_company:load_commercial_company_id(*),
        unload_commercial_company:unload_commercial_company_id(*),
        load_place_company:load_place_company_id(*),
        unload_place_company:unload_place_company_id(*),
        exporter_company:exporter_company_id(*),
        importer_company:importer_company_id(*)`)
      .eq('edpr_document_id', documentId)
      .eq('is_deleted', false);
    if (error) throw error;
    return (data ?? []) as unknown as EdprRecord[];
  }

  /** Lightweight poll — returns the prepared timestamp (or null) for one doc. */
  async getPreparedAt(documentId: number): Promise<string | null> {
    const { data, error } = await this.sb
      .from('edpr_documents')
      .select('docs_prepared_at')
      .eq('id', documentId)
      .single();
    if (error) throw error;
    return (data as { docs_prepared_at: string | null } | null)?.docs_prepared_at ?? null;
  }

  /**
   * Count non-deleted EDPR documents linked to a given request.
   * Returns 0 when requestId is null/undefined.
   */
  async countByRequest(requestId: number): Promise<number> {
    const { count, error } = await this.sb
      .from('edpr_documents')
      .select('id', { count: 'exact', head: true })
      .eq('is_deleted', false)
      .eq('edpr_request_id', requestId);
    if (error) throw error;
    return count ?? 0;
  }
}
